// components/UnifiedAddToCartButton.tsx
"use client";
import { useTranslations } from "next-intl";
import { Eye, XCircle, Bell, Pencil, Check } from "lucide-react";
import { CommerceIcons } from "@/lib/icons/commerce-icons";
import { Button } from "@/components/ui/button";
import { useCartContext } from "@/components/cart/cart-provider";
import { useAddToCart } from "@/hooks/use-add-to-cart";
import { useEffect, useMemo, useState, useRef } from "react";
import { NotificationConfirmationModal } from "@/components/search/product/notification-confirmation-modal";
import { useScheduledCountdown } from "@/hooks/use-scheduled-countdown";
import { cn, formatPrice } from "@/lib/utils";
import { NotificationPopover } from "@/components/ui/notification-popover";
import { DEFAULT_CURRENCY } from "@/lib/currencies";
import { itemCardStatusFromListing } from "@/lib/item-card-status";
import { MakeOfferButton } from "@/components/offers/make-offer-button";
import { editListingEvents } from "@/lib/edit-listing-events";
import { useOverlay } from "@/components/context/overlay-context";

function ItemCardBuyNowLabel({ preorder = false }: { preorder?: boolean }) {
  const t = useTranslations("addToCart");
  return (
    <span className="min-w-0 truncate">
      {preorder ? t("preorderShort") : t("buy")}
    </span>
  );
}

const itemCardCtaText = "whitespace-nowrap";

interface UnifiedAddToCartButtonProps {
  listingId: string;
  price: string;
  currency?: string | null;
  stock?: number;
  currentUserId?: string;
  name?: string;
  images?: string[];
  sellerId?: string;
  sellerName?: string;
  sellerAvatarUrl?: string;
  condition?: string;
  description?: string;
  shippingMethods?: any[];
  variant?: "default" | "productFooter";
  context?: "feed" | "product-page" | "saved-listings";
  uiVariant?: "default" | "feed-light";
  uiLayout?: "default" | "inline";
  // Option pour activer le mode lazy loading
  lazy?: boolean;
  scheduledPublishAt?: string | null;
  sellerBanned?: boolean;
  isUnlisted?: boolean;
  buyNow?: boolean;
  onLoginRequiredForBuyNow?: () => void;
  itemCard?: boolean;
  listingFormat?: string | null;
  discoverTypeLabel?: string | null;
  listingType?: "standard" | "preorder" | null;
  splitCommerce?: { mode: "cart" | "offer"; onModeChange: (m: "cart" | "offer") => void } | null;
  splitBarCompact?: boolean;
  // Phase 3 (2026-06-29): `acceptsOffers` prop dropped — derive from
  // `listingFormat` via `isOfferEnabledFormat()` (the column collapsed).
}

export function UnifiedAddToCartButton({ 
  listingId, 
  price, 
  currency,
  stock = Infinity, 
  currentUserId,
  name = "Unknown Item",
  images = [],
  sellerId = "",
  sellerName = "Unknown Seller",
  sellerAvatarUrl,
  condition = "used",
  description = "",
  shippingMethods = [],
  variant = "default",
  context = "feed",
  uiVariant = "default",
  uiLayout = "default",
  lazy = false,
  scheduledPublishAt = null,
  sellerBanned = false,
  isUnlisted = false,
  buyNow = false,
  onLoginRequiredForBuyNow,
  itemCard = false,
  listingFormat = null,
  discoverTypeLabel = null,
  listingType = null,
  splitCommerce = null,
  splitBarCompact = false,
}: UnifiedAddToCartButtonProps) {
  const tATC = useTranslations("addToCart");
  const { openUtility } = useOverlay();
  const userId = currentUserId ?? null;
  const currencyCode = (currency || DEFAULT_CURRENCY).toUpperCase();
  
  // État local pour le mode lazy (ne concerne que le rendu local, pas l'état du panier)
  const [isLazyInit, setIsLazyInit] = useState(!lazy);
  const [localQtyInCart, setLocalQtyInCart] = useState(0);
  // États pour le modal de notifications
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true); // Commence à true pendant le chargement initial
  const [isModalOpen, setIsModalOpen] = useState(false);
  const lastCheckKeyRef = useRef<string>(""); // Pour éviter les vérifications répétées
  const hasCheckedSubscriptionRef = useRef<boolean>(false); // Pour savoir si on a déjà vérifié au moins une fois
  
  // Hooks du panier
  const { cartItems, loadCart, isInitialized: isCartInitialized } = useCartContext();
  const { addToCart, adding, justAdded } = useAddToCart(userId);
  
  // Initialisation pour le mode lazy (uniquement pour ce composant)
  useEffect(() => {
    if (lazy && !isLazyInit) {
      setIsLazyInit(true);
    }
  }, [lazy, isLazyInit]);

  // Synchronisation état local optimiste (pour tous les modes)
  useEffect(() => {
    if (cartItems.length >= 0) {
      const qtyInCart = cartItems.find((item: any) => item.listingId === listingId)?.quantity || 0;
      setLocalQtyInCart(qtyInCart);
    }
  }, [cartItems, listingId]);

  // Calcul de la quantité en fonction du mode
  const qtyInCart = lazy ? localQtyInCart : cartItems.find((item: any) => item.listingId === listingId)?.quantity || 0;
  
  const soldOut = stock <= 0;
  const maxedOut = qtyInCart >= stock;
  const isInCart = qtyInCart > 0;
  const cartReady = isCartInitialized === true;
  const isPreorder = listingType === "preorder";
  const soldOutMessage = isPreorder ? tATC("preordersClosed") : tATC("outOfStock");
  const primaryCtaLabel = isPreorder ? tATC("preorder") : tATC("buyNow");
  const isProductPage = context === "product-page";
  const isCarousel = variant === "productFooter" && context === "feed";
  const isFeedLight = context === "feed" && uiVariant === "feed-light" && !isProductPage;
  const isInline = uiLayout === "inline";
  const feedButtonHeight = isInline ? "h-11" : "h-[50px]";
  const feedButtonMargin = isInline ? "mb-0" : "mb-4";
  const feedOnDarkMuted =
    "bg-white/15 text-white border border-white/35 backdrop-blur-sm";

  // État local pour scheduledPublishAt qui peut être mis à jour automatiquement
  const [currentScheduledPublishAt, setCurrentScheduledPublishAt] = useState<string | null>(scheduledPublishAt || null);
  
  // Récupérer le scheduled_publish_at depuis Supabase pour refléter les changements (hors feed)
  useEffect(() => {
    if (!listingId) return;
    if (context === "feed") return;
    if (!scheduledPublishAt) return;
    
    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 4000);

    const fetchScheduledTime = async () => {
      try {
        const response = await fetch(`/api/listings/${listingId}/scheduled-time`, { signal: controller.signal });
        if (response.ok) {
          const data = await response.json();
          if (data.scheduled_publish_at !== undefined) {
            if (!cancelled) setCurrentScheduledPublishAt(data.scheduled_publish_at);
          }
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        console.error('Error fetching scheduled time:', error);
      }
    };
    
    fetchScheduledTime();

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      try { controller.abort(); } catch {}
    };
  }, [listingId, context, scheduledPublishAt]);
  
  // Mettre à jour si la prop change (par exemple lors d'un refresh)
  useEffect(() => {
    if (scheduledPublishAt !== undefined) {
      setCurrentScheduledPublishAt(scheduledPublishAt);
    }
  }, [scheduledPublishAt]);
  
  // Scheduled countdown logic - utilise le hook pour détecter automatiquement la fin du countdown
  const { isScheduled, countdownExpired, isMounted: isCountdownMounted } = useScheduledCountdown(currentScheduledPublishAt);

  // Vérifier l'état d'abonnement au montage si l'utilisateur est connecté
  // Ne vérifier qu'une seule fois par combinaison currentUserId + listingId + isScheduled
  useEffect(() => {
    const checkSubscription = async () => {
      // Si ce n'est pas un listing scheduled, pas besoin de vérifier
      if (!isScheduled) {
        setIsSubscribed(false);
        // Ne mettre à false que si on a déjà vérifié au moins une fois
        if (hasCheckedSubscriptionRef.current) {
          setIsLoadingSubscription(false);
        }
        lastCheckKeyRef.current = "";
        return;
      }

      // Si on n'a pas encore currentUserId, ne pas garder le skeleton
      // Le bouton doit être visible et cliquable pour afficher le modal de login
      if (!currentUserId) {
        setIsSubscribed(false);
        setIsLoadingSubscription(false);
        hasCheckedSubscriptionRef.current = true; // Marquer comme vérifié pour éviter de bloquer
        lastCheckKeyRef.current = "";
        return;
      }

      // Créer une clé unique pour cette combinaison
      const checkKey = `${currentUserId}-${listingId}-${isScheduled}`;
      
      // Si on a déjà vérifié cette même combinaison, ne pas re-vérifier
      if (lastCheckKeyRef.current === checkKey && hasCheckedSubscriptionRef.current) {
        setIsLoadingSubscription(false);
        return;
      }

      // Mettre à jour la clé avant l'appel pour éviter les appels multiples
      lastCheckKeyRef.current = checkKey;
      setIsLoadingSubscription(true); // Démarrer le chargement

      try {
        const response = await fetch(`/api/notifications/subscribe?listingId=${listingId}`);
        if (response.ok) {
          const data = await response.json();
          setIsSubscribed(data.isSubscribed || false);
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
        // En cas d'erreur, réinitialiser la clé pour permettre un retry
        lastCheckKeyRef.current = "";
      } finally {
        // Marquer qu'on a vérifié au moins une fois
        hasCheckedSubscriptionRef.current = true;
        setIsLoadingSubscription(false); // Terminer le chargement
      }
    };

    checkSubscription();
  }, [currentUserId, listingId, isScheduled]);

  // Type pour les unités de countdown
  type CountdownUnit = {
    value: number;
    label: string;
  };

  type CountdownDisplay = {
    units: CountdownUnit[];
  };

  // Calculer le countdown avec format séparé par unités
  const calculateCountdownWithLabels = (ms: number): CountdownDisplay => {
    let totalSeconds = Math.floor(ms / 1000);
    const weeks = Math.floor(totalSeconds / 604800); // 7*24*3600
    totalSeconds %= 604800;
    const days = Math.floor(totalSeconds / 86400);
    totalSeconds %= 86400;
    const hours = Math.floor(totalSeconds / 3600);
    totalSeconds %= 3600;
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    
    if (weeks > 0) {
      return {
        units: [
          { value: weeks, label: weeks !== 1 ? tATC("countdown.weeks") : tATC("countdown.week") },
          { value: days, label: days !== 1 ? tATC("countdown.days") : tATC("countdown.day") },
          { value: hours, label: hours !== 1 ? tATC("countdown.hours") : tATC("countdown.hour") },
        ]
      };
    } else if (days > 0) {
      return {
        units: [
          { value: days, label: days !== 1 ? tATC("countdown.days") : tATC("countdown.day") },
          { value: hours, label: hours !== 1 ? tATC("countdown.hours") : tATC("countdown.hour") },
          { value: minutes, label: minutes !== 1 ? tATC("countdown.minutes") : tATC("countdown.minute") },
        ]
      };
    } else {
      return {
        units: [
          { value: hours, label: hours !== 1 ? tATC("countdown.hours") : tATC("countdown.hour") },
          { value: minutes, label: minutes !== 1 ? tATC("countdown.minutes") : tATC("countdown.minute") },
          { value: seconds, label: seconds !== 1 ? tATC("countdown.seconds") : tATC("countdown.second") },
        ]
      };
    }
  };

  // Calculer le countdown initial immédiatement
  const calculateCountdown = useMemo(() => {
    if (!isScheduled || !currentScheduledPublishAt) return { units: [] };
    const target = new Date(currentScheduledPublishAt).getTime();
    const ms = Math.max(0, target - Date.now());
    return calculateCountdownWithLabels(ms);
  }, [isScheduled, currentScheduledPublishAt]);

  // Initialiser countdown avec une valeur vide pour éviter l'erreur d'hydration
  // Le countdown sera calculé uniquement côté client après l'hydration
  const [countdown, setCountdown] = useState<CountdownDisplay>({ units: [] });

  // Calculer le countdown pour l'affichage (seulement si le listing est encore programmé)
  useEffect(() => {
    // Calculer immédiatement le countdown après le montage pour éviter d'afficher "Scheduled"
    if (isScheduled && currentScheduledPublishAt && !countdownExpired && isCountdownMounted) {
      const target = new Date(currentScheduledPublishAt).getTime();
      const ms = Math.max(0, target - Date.now());
      
      // Si le temps est déjà écoulé au montage, ne rien afficher
      if (ms <= 0) {
        setCountdown({ units: [] });
        return;
      }
      
      setCountdown(calculateCountdownWithLabels(ms));
    } else {
      setCountdown({ units: [] });
    }
  }, [isScheduled, countdownExpired, currentScheduledPublishAt, isCountdownMounted]);

  useEffect(() => {
    if (!isScheduled || !currentScheduledPublishAt || !isCountdownMounted || countdownExpired) {
      if (!isScheduled || !currentScheduledPublishAt || countdownExpired) {
        setCountdown({ units: [] });
      }
      return;
    }
    const target = new Date(currentScheduledPublishAt).getTime();
    const tick = () => {
      // Si le countdown est terminé (via le hook), arrêter l'intervalle
      if (countdownExpired) {
        setCountdown({ units: [] });
        return;
      }
      
      const ms = Math.max(0, target - Date.now());
      
      // Si le temps est écoulé, arrêter l'affichage du countdown
      if (ms <= 0) {
        setCountdown({ units: [] });
        return;
      }
      
      setCountdown(calculateCountdownWithLabels(ms));
    };
    // Mettre à jour immédiatement avec la valeur calculée
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [isScheduled, currentScheduledPublishAt, isCountdownMounted, countdownExpired]);

  // NOTE: No early return based on cartReady to preserve hook order.
  // Use cartReady only to disable buttons and display a pulse style.

  const handleAdd = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (adding || soldOut || maxedOut || sellerBanned || isUnlisted) return;
    
    const normalizedImages = Array.isArray(images) ? images.filter(img => img && typeof img === 'string' && img.trim().length > 0) : [];

    try {
      await addToCart({
        listingId,
        name,
        price: parseFloat(price),
        currency: currencyCode,
        images: normalizedImages,
        sellerId: sellerId || "",
        sellerName: sellerName || "Unknown Seller",
        sellerAvatarUrl,
        condition,
        description,
        shippingMethods,
        maxQuantity: stock,
      });
    } catch {
      /* addError is surfaced via useAddToCart; button re-enables via finally */
    }
  };

  const handleSeeInCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    // `forceRefresh` = former `open-cart-force` semantics (spec §E step
    // 1.3): the item was just added, so bypass the 60s local cache and
    // pull the real cart rows instead of a possibly-stale snapshot.
    openUtility({ kind: "cart", forceRefresh: true });
  };

  const handleBuyNow = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (adding || soldOut || sellerBanned || isUnlisted) return;
    if (!currentUserId) {
      onLoginRequiredForBuyNow?.();
      return;
    }
    window.dispatchEvent(new CustomEvent("open-feed-buy-now-sheet", { detail: { listingId } }));
  };


  const handleConfirmNotification = async () => {
    if (!currentUserId) {
      setIsModalOpen(false);
      return;
    }

    try {
      const response = await fetch('/api/notifications/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ listingId }),
      });

      if (!response.ok) {
        const error = await response.json();
        console.error('Error subscribing to notification:', error);
        // Ne pas marquer comme abonné si l'API a échoué
        return;
      }

      // Mettre à jour l'état local ET marquer la clé pour éviter une re-vérification
      setIsSubscribed(true);
      // Mettre à jour la clé pour indiquer qu'on a vérifié et que l'utilisateur est abonné
      lastCheckKeyRef.current = `${currentUserId}-${listingId}-${isScheduled}`;
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error subscribing to notification:', error);
      // Ne pas marquer comme abonné en cas d'erreur
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const renderNotificationModal = () => {
    if (!isScheduled || !isModalOpen) return null;
    return (
      <NotificationConfirmationModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmNotification}
        currentUserId={currentUserId}
        listing={{
          id: listingId,
          title: name,
          price,
          location: "", // Non disponible dans ce contexte
          image_urls: images,
          description,
          condition,
          grade: "", // Non disponible dans ce contexte
          created_at: "", // Non disponible dans ce contexte
          username: sellerName,
          avatar_url: sellerAvatarUrl,
          scheduledPublishAt,
        }}
      />
    );
  };

  const addedPopover = (
    <NotificationPopover
      visible={justAdded && (context === "product-page" || context === "saved-listings" || context === "feed")}
      message={tATC("addedToCart")}
      position={context === "feed" || context === "saved-listings" ? "card" : "center"}
      listingId={context === "feed" || context === "saved-listings" ? listingId : undefined}
      duration={2000}
    />
  );

  const itemCardListingStatus = itemCard
    ? itemCardStatusFromListing({
        scheduled_publish_at: currentScheduledPublishAt,
        listing_format: listingFormat,
        listing_type: listingType,
        discoverTypeLabel: discoverTypeLabel ?? undefined,
      })
    : null;

  // Item-card CTA base. LAYOUT ONLY — no bg/text-color utility, because the
  // color comes from the Button `variant`: foil's ink text is `.foil-ink`
  // (@layer components) and any `text-*` utility (@layer utilities) would win
  // the cascade and clobber it. So foil CTAs use variant="foil" + this base;
  // the "done" state uses variant="secondary"; muted/ink add their own color.
  const itemCardBtnLayout =
    "inline-flex h-8 shrink-0 items-center justify-center gap-1 rounded-full px-2 text-[10.5px] font-semibold whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 shadow-none";
  // Ink treatment for non-commerce management CTAs (Edit) + muted states.
  const itemCardBtnClass = `${itemCardBtnLayout} transition-colors bg-foreground text-background hover:bg-foreground/90`;

  if (itemCard) {
    const isOwner = Boolean(currentUserId && sellerId && currentUserId === sellerId);
    if (isOwner) {
      return (
        <Button
          type="button"
          variant="default"
          size="sm"
          className={itemCardBtnClass}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            editListingEvents.open({
              listingId,
              listing: {
                id: listingId,
                title: name,
                price,
                currency,
                image_urls: images,
                description,
                condition,
                listing_format: listingFormat,
              },
              messageId: "",
              roomName: "",
            });
          }}
        >
          <Pencil className="size-3.5 shrink-0" />
          <span className={itemCardCtaText}>{tATC("edit")}</span>
        </Button>
      );
    }
    if (sellerBanned) {
      return (
        <span
          className={`${itemCardBtnClass} cursor-default bg-muted text-muted-foreground`}
          onClick={(e) => e.stopPropagation()}
        >
          <span className={itemCardCtaText}>{tATC("unavailable")}</span>
        </span>
      );
    }
    if (isUnlisted) {
      return (
        <span
          className={`${itemCardBtnClass} cursor-default bg-muted text-muted-foreground`}
          onClick={(e) => e.stopPropagation()}
        >
          {tATC("unlisted")}
        </span>
      );
    }
    if (isScheduled && !countdownExpired) {
      return (
        <>
          <Button
            type="button"
            variant={isSubscribed ? "secondary" : "foil"}
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              if (!isSubscribed && !isLoadingSubscription) {
                setIsModalOpen(true);
              }
            }}
            disabled={isSubscribed || isLoadingSubscription}
            className={`${itemCardBtnLayout} disabled:opacity-60`}
          >
            {isLoadingSubscription ? null : isSubscribed ? (
              <Check className="size-3.5 shrink-0" />
            ) : (
              <Bell className="size-3.5 shrink-0" />
            )}
            <span className={itemCardCtaText}>
              {isLoadingSubscription ? "…" : isSubscribed ? tATC("subscribed") : tATC("notify")}
            </span>
          </Button>
          {renderNotificationModal()}
        </>
      );
    }
    if (soldOut) {
      return (
        <span
          className={`${itemCardBtnClass} cursor-default bg-muted text-muted-foreground`}
          onClick={(e) => e.stopPropagation()}
        >
          {tATC("soldOut")}
        </span>
      );
    }
    if (itemCardListingStatus === "auction") {
      return (
        <Button
          type="button"
          variant="foil"
          size="sm"
          className={itemCardBtnLayout}
          onClick={(e) => {
            e.stopPropagation();
            openUtility({ kind: "auction-bid", listingId, description: description || null });
          }}
        >
          <CommerceIcons.bid className="size-3.5 shrink-0" />
          <span className={itemCardCtaText}>{tATC("bid")}</span>
        </Button>
      );
    }
    if (splitCommerce) {
      const isBuyNowItem = buyNow && context === "feed";
      const cartActive = splitCommerce.mode === "cart";
      const offerActive = splitCommerce.mode === "offer";
      return (
        <div className="flex w-full min-w-0 max-w-full items-stretch gap-1.5" onClick={(e) => e.stopPropagation()}>
          <Button
            type="button"
            variant={cartActive ? "foil" : "ghost"}
            size="sm"
            className={`${itemCardBtnLayout} min-w-0 flex-1 justify-center ${
              cartActive ? "" : "bg-muted/80 text-muted-foreground hover:bg-muted"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              if (cartActive) {
                if (isInCart) {
                  if (isBuyNowItem) {
                    void handleBuyNow(e);
                  } else {
                    void handleSeeInCart(e);
                  }
                } else {
                  if (isBuyNowItem) {
                    void handleBuyNow(e);
                  } else {
                    void handleAdd(e);
                  }
                }
              } else {
                splitCommerce.onModeChange("cart");
              }
            }}
            disabled={
              cartActive &&
              (isInCart
                ? isBuyNowItem
                  ? adding || soldOut || sellerBanned || isUnlisted
                  : false
                : isBuyNowItem
                  ? adding || soldOut || sellerBanned || isUnlisted || !currentUserId
                  : !cartReady || adding || maxedOut)
            }
          >
            {isInCart && !isBuyNowItem ? (
              <Eye className="size-3.5 shrink-0" />
            ) : isBuyNowItem ? (
              <CommerceIcons.buyNow className="size-3.5 shrink-0" />
            ) : (
              <CommerceIcons.addToCart className="size-3.5 shrink-0" />
            )}
            <span className={itemCardCtaText}>
              {isInCart
                ? isBuyNowItem
                  ? <ItemCardBuyNowLabel preorder={isPreorder} />
                  : tATC("seeInCart")
                : isBuyNowItem
                  ? <ItemCardBuyNowLabel preorder={isPreorder} />
                  : tATC("add")}
            </span>
          </Button>
          <Button
            type="button"
            variant={offerActive ? "foil" : "ghost"}
            size="sm"
            className={`${itemCardBtnLayout} min-w-0 flex-1 justify-center ${
              offerActive ? "" : "bg-muted/80 text-muted-foreground hover:bg-muted"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              splitCommerce.onModeChange("offer");
            }}
          >
            <CommerceIcons.offer className="size-3.5 shrink-0" />
            <span className={itemCardCtaText}>{tATC("offer")}</span>
          </Button>
        </div>
      );
    }
    if (itemCardListingStatus === "offers") {
      const numericPrice = Number(price);
      const offerPrice = Number.isFinite(numericPrice) ? numericPrice : null;
      return (
        <div className="contents [&_button]:h-8 [&_button]:max-w-full [&_button]:min-w-0 [&_button]:rounded-full [&_button]:px-2 [&_button]:text-[10.5px] [&_button]:font-medium" onClick={(e) => e.stopPropagation()}>
          <MakeOfferButton
            listingId={listingId}
            currentUserId={currentUserId ?? null}
            sellerId={sellerId}
            // Phase 3 (2026-06-29): MakeOfferButton derives "offers enabled"
            // from `listingFormat` via `isOfferEnabledFormat()`.
            listingFormat={listingFormat === "offers_only" ? "offers_only" : "fixed_price_with_offers"}
            listingPrice={offerPrice}
            currency={currency ?? null}
            listingTitle={name}
            listingImageUrl={images[0] ?? null}
            listingCondition={condition}
            sellerUsername={sellerName}
            variant="foil"
            className={itemCardBtnLayout}
            fullWidth={false}
          />
        </div>
      );
    }
    if (isInCart) {
      const isBuyNowItem = buyNow && context === "feed";
      return (
        <>
          <Button
            type="button"
            variant="foil"
            size="sm"
            className={itemCardBtnLayout}
            onClick={isBuyNowItem ? handleBuyNow : handleSeeInCart}
          >
            {isBuyNowItem ? (
              <CommerceIcons.buyNow className="size-3.5 shrink-0" />
            ) : (
              <Eye className="size-3.5 shrink-0" />
            )}
            {isBuyNowItem ? <ItemCardBuyNowLabel preorder={isPreorder} /> : <span className={itemCardCtaText}>{tATC("seeInCart")}</span>}
          </Button>
          {addedPopover}
        </>
      );
    }
    const isBuyNowItem = buyNow && context === "feed";
    return (
      <>
        <Button
          type="button"
          variant="foil"
          size="sm"
          data-listing-id={listingId}
          onClick={isBuyNowItem ? handleBuyNow : handleAdd}
          disabled={!isBuyNowItem && (!cartReady || adding || maxedOut)}
          className={cn(
            itemCardBtnLayout,
            !isBuyNowItem && !cartReady ? "animate-pulse opacity-60" : "",
          )}
        >
          {isBuyNowItem ? (
            <CommerceIcons.buyNow className="size-3.5 shrink-0" />
          ) : (
            <CommerceIcons.addToCart className="size-3.5 shrink-0" />
          )}
          {isBuyNowItem ? (
            <ItemCardBuyNowLabel preorder={isPreorder} />
          ) : (
            <span className={itemCardCtaText}>
              {adding ? "…" : maxedOut ? tATC("max") : tATC("add")}
            </span>
          )}
        </Button>
        {addedPopover}
        {renderNotificationModal()}
      </>
    );
  }

  if (sellerBanned) {
    if (variant === "productFooter") {
      return (
        <div className="relative w-full mb-4">
          <div
            className={`w-full rounded-full overflow-hidden p-1 h-12 sm:h-14 flex items-center gap-2
                       ${isCarousel ? 'bg-white/10 backdrop-blur-md border-0' : (isProductPage ? 'bg-black/5 dark:bg-white/10 backdrop-blur-md border border-black/10 dark:border-0' : 'bg-white/10 backdrop-blur-md border-0')}`}
          >
            <div className="min-w-0 flex-1 h-full px-5 sm:px-6 flex items-center justify-start overflow-hidden">
              <span className={`text-lg sm:text-xl font-semibold tracking-tight truncate ${isCarousel ? 'text-white' : ''}`}>{formatPrice(price, currencyCode)}</span>
            </div>
            <Button
              data-listing-id={listingId}
              disabled
              onClick={(e) => e.stopPropagation()}
              className="h-full rounded-full font-semibold px-6 sm:px-8 flex items-center gap-2 shrink-0 bg-muted/80 text-muted-foreground border border-border cursor-not-allowed"
            >
              <CommerceIcons.addToCart size={18} />
              {tATC("banished")}
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="w-full h-[50px] rounded-full flex items-center justify-center gap-2 mb-4 font-medium border backdrop-blur-md bg-muted/80 text-muted-foreground border-border cursor-not-allowed">
        <CommerceIcons.addToCart className="h-4 w-4" />
        {tATC("banished")}
      </div>
    );
  }

  if (isUnlisted) {
    if (variant === "productFooter") {
      return (
        <div className="relative w-full mb-4">
          <div
            className={`w-full rounded-full overflow-hidden p-1 h-12 sm:h-14 flex items-center gap-2
                       ${isCarousel ? 'bg-white/10 backdrop-blur-md border-0' : (isProductPage ? 'bg-black/5 dark:bg-white/10 backdrop-blur-md border border-black/10 dark:border-0' : 'bg-white/10 backdrop-blur-md border-0')}`}
          >
            <div className="min-w-0 flex-1 h-full px-5 sm:px-6 flex items-center justify-start overflow-hidden">
              <span className={`text-lg sm:text-xl font-semibold tracking-tight truncate ${isCarousel ? 'text-white' : ''}`}>{formatPrice(price, currencyCode)}</span>
            </div>
            <Button
              data-listing-id={listingId}
              disabled
              onClick={(e) => e.stopPropagation()}
              className="h-full rounded-full font-semibold px-6 sm:px-8 flex items-center gap-2 shrink-0 bg-muted/80 text-muted-foreground border border-border cursor-not-allowed"
            >
              <CommerceIcons.addToCart size={18} />
              {tATC("unlisted")}
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="w-full h-[50px] rounded-full flex items-center justify-center gap-2 mb-4 font-medium border backdrop-blur-md bg-muted/80 text-muted-foreground border-border cursor-not-allowed">
        <CommerceIcons.addToCart className="h-4 w-4" />
        {tATC("unlisted")}
      </div>
    );
  }

  if (isScheduled && !countdownExpired) {
    if (variant === "productFooter") {
      return (
        <>
        <div className="relative w-full mb-4">
          <div
            className={`w-full rounded-full overflow-hidden p-1 h-12 sm:h-14 flex items-center gap-2
                       ${isCarousel ? 'bg-white/10 backdrop-blur-md border-0' : (isProductPage ? 'bg-black/5 dark:bg-white/10 backdrop-blur-md border border-black/10 dark:border-0' : 'bg-white/10 backdrop-blur-md border-0')}`}
          >
            <div className="min-w-0 flex-1 h-full px-5 sm:px-6 flex items-center justify-start overflow-hidden">
              <span className={`text-lg sm:text-xl font-semibold tracking-tight truncate ${isCarousel ? 'text-white' : ''}`}>{formatPrice(price, currencyCode)}</span>
            </div>
            <Button
              data-listing-id={listingId}
              disabled
              onClick={(e) => e.stopPropagation()}
                className={`h-full rounded-full font-medium px-3 sm:px-4 flex items-center justify-center transition-transform active:scale-95 shrink-0 ${
                  !isCountdownMounted || !countdown.units.length
                    ? 'animate-pulse bg-muted border border-black/30 dark:border-white/30'
                    : isProductPage
                            ? 'bg-black text-white hover:bg-black/90 border border-black/30 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-black/40 dark:bg-white dark:text-black dark:border-white/30 dark:hover:bg-white/90 dark:focus-visible:ring-white/50'
                    : 'bg-black text-white hover:bg-black/90'
                }`}
            >
                {!isCountdownMounted || !countdown.units.length ? null : (
                  <div className="flex items-center justify-center gap-1 sm:gap-1.5">
                    {countdown.units.map((unit, index) => (
                      <div key={index} className="flex items-center gap-1 sm:gap-1.5">
                        <div className="flex flex-col items-center">
                          <div suppressHydrationWarning className="text-lg sm:text-xl font-bold leading-none">
                            {String(unit.value).padStart(2, "0")}
                          </div>
                          <div suppressHydrationWarning className="text-[9px] sm:text-[10px] -mt-0.5 sm:mt-0 opacity-80 font-medium leading-none">
                            {unit.label}
                          </div>
                        </div>
                        {index < countdown.units.length - 1 && (
                          <div className="text-base sm:text-lg font-bold opacity-60 pb-1 sm:pb-2">:</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </Button>
              {isScheduled && (
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!isSubscribed && !isLoadingSubscription) {
                      setIsModalOpen(true);
                    }
                  }}
                  disabled={isSubscribed || isLoadingSubscription}
                  className={`h-full w-12 sm:w-14 rounded-full backdrop-blur-md border-2 transition-all shrink-0 ${
                    isLoadingSubscription
                      ? 'animate-pulse bg-muted border-white/30 shadow-none cursor-wait'
                      : isSubscribed
                      ? 'bg-white/10 text-white border-white/20 shadow-none opacity-60 cursor-not-allowed'
                      : 'bg-white text-black border-white shadow-lg shadow-white/30 hover:shadow-white/40 active:scale-95 cursor-pointer'
                  }`}
                  aria-label={isSubscribed ? tATC("notificationsEnabled") : isLoadingSubscription ? tATC("checkingSubscription") : tATC("getNotified")}
                >
                  {isLoadingSubscription ? null : (
                    <Bell className={`h-5 w-5 sm:h-6 sm:w-6 ${isSubscribed ? 'fill-white' : 'fill-none'}`} />
                  )}
            </Button>
              )}
            </div>
          </div>
          {addedPopover}
          {renderNotificationModal()}
        </>
      );
    }
    // Variante par défaut (bouton plein large)
    return (
      <>
        <div className="relative w-full flex gap-2">
        <Button
          data-listing-id={listingId}
          disabled
          onClick={(e) => e.stopPropagation()}
          className={
              `flex-1 h-[50px] rounded-full flex items-center justify-center mb-4 active:scale-[0.98] transition-transform ${
                !isCountdownMounted || !countdown.units.length
                  ? 'animate-pulse bg-muted border border-black/30 dark:border-white/30'
                  : 'bg-black text-white hover:bg-black/90 border border-black/30 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-black/40 dark:bg-white dark:text-black dark:border-white/30 dark:hover:bg-white/90 dark:focus-visible:ring-white/50'
              }`
          }
        >
            {!isCountdownMounted || !countdown.units.length ? null : (
              <div className="flex items-center justify-center gap-1.5">
                {countdown.units.map((unit, index) => (
                  <div key={index} className="flex items-center gap-1.5">
                    <div className="flex flex-col items-center">
                      <div suppressHydrationWarning className="text-xl font-bold leading-none">
                        {String(unit.value).padStart(2, "0")}
                      </div>
                      <div suppressHydrationWarning className="text-[10px] mt-0.5 opacity-80 font-medium leading-none">
                        {unit.label}
                      </div>
                    </div>
                    {index < countdown.units.length - 1 && (
                      <div className="text-lg font-bold opacity-60 pb-3">:</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Button>
          {isScheduled && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                if (!isSubscribed && !isLoadingSubscription) {
                  setIsModalOpen(true);
                }
              }}
              disabled={isSubscribed || isLoadingSubscription}
              className={`h-[50px] w-[50px] rounded-full mb-4 shrink-0 transition-all ${
                isLoadingSubscription
                  ? 'animate-pulse bg-muted border border-black/30 dark:border-white/30 cursor-wait'
                  : isSubscribed
                  ? 'bg-black/10 text-black border border-black/20 dark:bg-white/10 dark:text-white dark:border-white/20 opacity-60 cursor-not-allowed'
                  : 'bg-black text-white border border-black/30 hover:bg-black/90 dark:bg-white dark:text-black dark:border-white/30 dark:hover:bg-white/90 active:scale-95 cursor-pointer'
              }`}
              aria-label={isSubscribed ? tATC("notificationsEnabled") : isLoadingSubscription ? tATC("checkingSubscription") : tATC("getNotified")}
            >
              {isLoadingSubscription ? null : (
                <Bell className={`h-5 w-5 ${isSubscribed ? 'fill-current' : 'fill-none'}`} />
              )}
        </Button>
          )}
      </div>
        {addedPopover}
        {renderNotificationModal()}
      </>
    );
  }

  // 2) Rupture de stock
  if (soldOut) {
    if (variant === "productFooter") {
      // Segmented glass container with disabled CTA
      return (
        <div className="relative w-full mb-4">
          <div
            className={`w-full rounded-full overflow-hidden p-1 h-12 sm:h-14 flex items-center gap-2
                       ${isCarousel ? 'bg-white/10 backdrop-blur-md border-0' : (isProductPage ? 'bg-black/5 dark:bg-white/10 backdrop-blur-md border border-black/10 dark:border-0' : 'bg-white/10 backdrop-blur-md border-0')}`}
          >
            <div className="min-w-0 flex-1 h-full px-5 sm:px-6 flex items-center justify-start overflow-hidden">
              <span className={`text-lg sm:text-xl font-semibold tracking-tight truncate ${isCarousel ? 'text-white' : ''}`}>{formatPrice(price, currencyCode)}</span>
            </div>
            <Button
              data-listing-id={listingId}
              className={`h-full rounded-full font-semibold px-6 sm:px-8 flex items-center gap-2 transition-transform active:scale-95 shrink-0
                         ${isCarousel ? 'bg-white/15 text-white hover:bg-white/20' : 'bg-black text-white hover:bg-black/90 border border-black/30 dark:bg-white dark:text-black dark:hover:bg-white/90 dark:border-white/30'}`}
              disabled
              onClick={(e) => e.stopPropagation()}
            >
              <XCircle size={18} />
              {soldOutMessage}
            </Button>
          </div>
        </div>
      );
    }
    // Style compact pour saved-listings (haute lisibilité)
  if (context === "saved-listings") {
      return (
  <div className="w-full text-center h-8 md:h-9 px-3 md:px-4 mb-0 font-medium whitespace-nowrap flex items-center justify-center text-xs md:text-sm
      rounded-full gap-1.5 border backdrop-blur-md
      bg-neutral-300/70 text-neutral-500 border-neutral-400 dark:bg-neutral-800/60 dark:text-neutral-200 dark:border-neutral-700">
          <XCircle className="h-3.5 w-3.5" />
          {soldOutMessage}
        </div>
      );
    }
    // Style dédié product-page (haute lisibilité)
  if (context === "product-page") {
      return (
  <div className="w-full h-[50px] rounded-full flex items-center justify-center gap-2 mb-4 whitespace-nowrap font-medium border backdrop-blur-md
      bg-neutral-300/70 text-neutral-500 border-neutral-400 dark:bg-neutral-800/60 dark:text-neutral-200 dark:border-neutral-700">
          <XCircle className="h-4 w-4" />
          {soldOutMessage}
        </div>
      );
    }
    // Feed et autres: style glass d'origine
    return (
      <div
        className={`w-full text-center ${isInline ? "py-2" : "py-3"} ${feedButtonMargin} font-medium whitespace-nowrap
                    ${isFeedLight ? feedOnDarkMuted : "bg-white/10 text-white border border-white/20"}
                    backdrop-blur-md rounded-full flex items-center justify-center gap-2`}
      >
        <XCircle className="h-4 w-4" />
        {soldOutMessage}
      </div>
    );
  }

  const isSavedListings = context === "saved-listings"
  const isSplitBarFeedOrPdp =
    splitCommerce && (context === "product-page" || context === "feed") && !isSavedListings

  if (isInCart && !isSplitBarFeedOrPdp) {
    if (variant === "productFooter") {
      return (
        <>
        <div className="relative w-full">
          <div
            className={`w-full rounded-full overflow-hidden p-1 h-12 sm:h-14 flex items-center gap-2 mb-4
                       ${isCarousel ? 'bg-white/10 backdrop-blur-md border-0' : (isProductPage ? 'bg-black/5 dark:bg-white/10 backdrop-blur-md border border-black/10 dark:border-0' : 'bg-white/10 backdrop-blur-md border-0')}`}
          >
            <div className="min-w-0 flex-1 h-full px-5 sm:px-6 flex items-center justify-start overflow-hidden">
              <span className={`text-lg sm:text-xl font-semibold tracking-tight truncate ${isCarousel ? 'text-white' : ''}`}>{formatPrice(price, currencyCode)}</span>
            </div>
            <Button
              variant="foil"
              data-listing-id={listingId}
              onClick={handleSeeInCart}
              className="h-full rounded-full font-semibold px-6 sm:px-8 flex items-center gap-2 shrink-0"
            >
              <Eye className="h-5 w-5" />
              {tATC("seeInCart")}
            </Button>
          </div>
        </div>
        {addedPopover}
        </>
      );
    }
    return (
      <>
      <div className="relative w-full">
        <Button
          variant="foil"
          onClick={buyNow ? handleBuyNow : handleSeeInCart}
          className={`w-full ${isProductPage ? "h-[50px]" : feedButtonHeight} rounded-full flex items-center justify-center gap-2 ${isProductPage ? "mb-4" : feedButtonMargin} whitespace-nowrap transition-transform active:scale-[0.98]`}
        >
          {buyNow ? <CommerceIcons.buyNow className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          <span className="font-medium whitespace-nowrap">{buyNow ? primaryCtaLabel : tATC("seeInCart")}</span>
        </Button>
      </div>
      {addedPopover}
      </>
    );
  }

  if (variant === "productFooter") {
    return (
      <>
      <div className="relative w-full">
        <div
          className={`w-full rounded-full overflow-hidden p-1 h-12 sm:h-14 flex items-center gap-2 mb-4
                     ${isCarousel ? 'bg-white/10 backdrop-blur-md border-0' : (isProductPage ? 'bg-black/5 dark:bg-white/10 backdrop-blur-md border border-black/10 dark:border-0' : 'bg-white/10 backdrop-blur-md border-0')} ${(!cartReady || adding || maxedOut) ? "opacity-60" : ""}
                     ${!cartReady ? "animate-pulse" : ""}`}
        >
          <div className="min-w-0 flex-1 h-full px-5 sm:px-6 flex items-center justify-start overflow-hidden">
            <span className={`text-lg sm:text-xl font-semibold tracking-tight truncate leading-none -translate-y-0.5 ${isCarousel ? 'text-white' : ''}`}>{formatPrice(price, currencyCode)}</span>
          </div>
          <Button
            variant="foil"
            data-listing-id={listingId}
            onClick={handleAdd}
            disabled={!cartReady || adding || maxedOut}
            className="h-full rounded-full font-semibold px-6 sm:px-8 flex items-center gap-2 shrink-0"
          >
            <CommerceIcons.addToCart className="h-5 w-5" />
            {isPreorder ? tATC("preorder") : tATC("addToCart")}
          </Button>
        </div>
      </div>
      {addedPopover}
      </>
    );
  }

  const isBuyNowMode = buyNow && context === "feed"
  const defaultAddLabel = isPreorder ? tATC("preorder") : tATC("addToCart")

  if (isSplitBarFeedOrPdp) {
    const cartActive = splitCommerce.mode === "cart"
    const offerActive = splitCommerce.mode === "offer"
    const height = splitBarCompact
      ? "h-11"
      : isProductPage
        ? "h-[50px]"
        : feedButtonHeight
    const margin = splitBarCompact ? "mb-0" : isProductPage ? "mb-4" : feedButtonMargin
    const iconS = splitBarCompact ? "h-4 w-4" : "h-5 w-5"
    const textS = splitBarCompact ? "text-sm" : ""
    // Feed: the active side is the one possible action → foil (via variant);
    // the inactive side stays muted. Product-page keeps its current treatment
    // (product-page redesign is a follow-on branch); its offer keeps amber.
    // The active side is the one possible action → foil everywhere (feed AND
    // product page); the inactive side stays muted.
    const cartVariant = cartActive ? "foil" : "ghost"
    const offerVariant = offerActive ? "foil" : "ghost"
    const cartClass = cartActive
      ? ""
      : isProductPage
        ? "bg-black/5 text-muted-foreground border border-border/50 hover:bg-black/10 dark:bg-white/5"
        : isFeedLight
          ? feedOnDarkMuted
          : "bg-white/20 text-white/80 border border-white/15"
    const offerClass = offerActive
      ? ""
      : isProductPage
        ? "bg-black/5 text-muted-foreground border border-border/50 hover:bg-black/10 dark:bg-white/5"
        : isFeedLight
          ? feedOnDarkMuted
          : "bg-white/20 text-white/80 border border-white/15"
    return (
      <>
        <div className={`relative w-full items-stretch flex gap-2 ${margin}`}>
          <Button
            variant={cartVariant}
            onClick={(e) => {
              e.stopPropagation()
              if (cartActive) {
                if (isInCart) {
                  if (isBuyNowMode) {
                    void handleBuyNow(e)
                  } else {
                    void handleSeeInCart(e)
                  }
                } else {
                  if (isBuyNowMode) {
                    void handleBuyNow(e)
                  } else {
                    void handleAdd(e)
                  }
                }
              } else {
                splitCommerce.onModeChange("cart")
              }
            }}
            disabled={
              cartActive &&
              (isInCart
                ? isBuyNowMode
                  ? adding || soldOut || sellerBanned || isUnlisted
                  : false
                : isBuyNowMode
                  ? adding || soldOut || sellerBanned || isUnlisted || !currentUserId
                  : !cartReady || adding || maxedOut || soldOut || sellerBanned || isUnlisted)
            }
            data-listing-id={listingId}
            className={`flex-1 min-w-0 ${height} rounded-full flex items-center justify-center gap-1.5 sm:gap-2 whitespace-nowrap font-medium transition-all duration-200 ease-out active:scale-[0.98] ${textS} ${cartClass} ${
              cartActive && !isInCart && (isBuyNowMode ? false : !cartReady)
                ? " animate-pulse opacity-60 cursor-not-allowed"
                : ""
            }`}
          >
            {isInCart && !isBuyNowMode ? (
              <Eye className={`shrink-0 ${iconS}`} />
            ) : isBuyNowMode ? (
              <CommerceIcons.buyNow className={`shrink-0 ${iconS}`} />
            ) : (
              <CommerceIcons.addToCart className={`shrink-0 ${iconS}`} />
            )}
            <span className="min-w-0 truncate">
              {isInCart
                ? isBuyNowMode
                  ? primaryCtaLabel
                  : tATC("seeInCart")
                : isBuyNowMode
                  ? primaryCtaLabel
                  : defaultAddLabel}
            </span>
          </Button>
          <Button
            variant={offerVariant}
            onClick={(e) => {
              e.stopPropagation()
              splitCommerce.onModeChange("offer")
            }}
            className={`flex-1 min-w-0 ${height} rounded-full flex items-center justify-center gap-1.5 sm:gap-2 font-medium transition-all duration-200 ease-out active:scale-[0.98] ${textS} ${offerClass}`}
            type="button"
          >
            <CommerceIcons.offer className={`shrink-0 ${iconS}`} />
            <span className="min-w-0 truncate">{tATC("makeOfferCta")}</span>
          </Button>
        </div>
        {addedPopover}
        {renderNotificationModal()}
      </>
    )
  }

  return (
    <>
    <div className="relative w-full">
      <Button
        variant="foil"
        onClick={isBuyNowMode ? handleBuyNow : handleAdd}
        disabled={!isBuyNowMode && (!cartReady || adding || maxedOut)}
        data-listing-id={listingId}
        className={`w-full ${isProductPage ? "h-[50px]" : feedButtonHeight} rounded-full flex items-center justify-center gap-2 ${isProductPage ? "mb-4" : feedButtonMargin} whitespace-nowrap active:scale-[0.98] transition-transform
                    ${!isBuyNowMode && !cartReady ? " animate-pulse opacity-60 cursor-not-allowed" : ""}`}
      >
        {isBuyNowMode ? (
          <CommerceIcons.buyNow className="h-5 w-5" />
        ) : (
          <CommerceIcons.addToCart className="h-5 w-5" />
        )}
        {isBuyNowMode ? primaryCtaLabel : defaultAddLabel}
      </Button>
    </div>
      {addedPopover}
      {renderNotificationModal()}
    </>
  );
}

// Export par défaut et alias pour compatibilité
export default UnifiedAddToCartButton;

// Aliases pour faciliter la migration
export const AddToCartButton = UnifiedAddToCartButton;
export const LazyAddToCartButton = (props: Omit<UnifiedAddToCartButtonProps, 'lazy'>) => 
  <UnifiedAddToCartButton {...props} lazy={true} />;
